import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';

import { stripeConfig } from '@/src/config/env';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

type VerifiedAccountEmbeddedCheckoutProps = {
  clientSecret: string | null;
  onComplete: () => void;
};

const stripePromise = stripeConfig.publishableKey
  ? loadStripe(stripeConfig.publishableKey)
  : null;

export function VerifiedAccountEmbeddedCheckout({
  clientSecret,
  onComplete,
}: VerifiedAccountEmbeddedCheckoutProps) {
  const options = useMemo(
    () =>
      clientSecret
        ? {
            clientSecret,
            onComplete,
          }
        : null,
    [clientSecret, onComplete],
  );

  if (!clientSecret) {
    return null;
  }

  if (!stripePromise || !options) {
    return (
      <View style={styles.errorCard}>
        <Text style={styles.errorTitle}>Stripe payment form is not ready.</Text>
        <Text style={styles.errorDescription}>
          Add EXPO_PUBLIC_STRIPE_PUBLISHABLE_KEY to the web deployment
          environment so Frienzzi can load embedded Stripe Checkout.
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.checkoutFrame}>
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </View>
  );
}

const styles = StyleSheet.create({
  checkoutFrame: {
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    overflow: 'hidden',
    padding: spacing.sm,
  },
  errorCard: {
    backgroundColor: colors.surfaceTint,
    borderColor: colors.danger,
    borderRadius: 8,
    borderWidth: 1,
    gap: spacing.xs,
    padding: spacing.md,
  },
  errorDescription: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
  },
  errorTitle: {
    color: colors.danger,
    fontSize: 15,
    fontWeight: '900',
  },
});
